import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import { GrafikSeluruhKecamatan } from "./GrafikSeluruhKecamatan";
import Footer from "./Footer";
import { Key, FileText, BookOpen, Mic, HomeIcon } from "lucide-react";

const SLIDES = [
  { src: "/images/map1.jpg", alt: "Peta Wilayah 1" },
  { src: "/images/map2.jpg", alt: "Peta Wilayah 2" },
  { src: "/images/map3.jpg", alt: "Peta Wilayah 3" },
  { src: "/images/map4.jpg", alt: "Peta Wilayah 4" },
  { src: "/images/map5.jpg", alt: "Peta Wilayah 5" },
];

const STATS = [
  { id: "total",  label: "Rumah Terdata",     target: 1985, suffix: "",  color: "#00B4A6" },
  { id: "layak",  label: "Rumah Layak Huni",  target: 1430, suffix: "",  color: "#60A5FA" },
  { id: "persen", label: "Tingkat Kelayakan", target: 72,   suffix: "%", color: "#FBBF24" },
  { id: "kec",    label: "Kecamatan",         target: 7,    suffix: "",  color: "#A78BFA" },
];

function useCounter(target, suffix, duration = 1400, started = false) {
  const [val, setVal] = useState("0" + suffix);
  useEffect(() => {
    if (!started) return;
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target).toLocaleString("id-ID") + suffix);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, target, suffix, duration]);
  return val;
}

function StatCounter({ label, target, suffix, color, started }) {
  const val = useCounter(target, suffix, 1400, started);
  return (
    <div className="sipkr-stat-item">
      <span className="sipkr-stat-num" style={{ color }}>{val}</span>
      <span className="sipkr-stat-label">{label}</span>
    </div>
  );
}

export default function Home() {
  const [current, setCurrent] = useState(0);
  const [statsStarted, setStatsStarted] = useState(false);
  const statsRef = useRef(null);
  const intervalRef = useRef(null);

  // Auto-advance slideshow
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((c) => (c + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Trigger counters when stats bar enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const goTo = (idx) => {
    clearInterval(intervalRef.current);
    setCurrent(idx);
    intervalRef.current = setInterval(() => {
      setCurrent((c) => (c + 1) % SLIDES.length);
    }, 5000);
  };

  return (
    <>
      {/* ── HERO ── */}
      <section className="sipkr-hero" aria-label="Halaman utama SIPKR">

        {/* Slideshow background */}
        <div className="sipkr-slides" aria-hidden="true">
          {SLIDES.map((s, i) => (
            <div
              key={i}
              className={`sipkr-slide ${i === current ? "sipkr-slide--active" : ""}`}
              style={{ backgroundImage: `url(${s.src})` }}
            />
          ))}
          {/* Dark scrim so text stays readable */}
          <div className="sipkr-scrim" />
        </div>

        {/* Dot navigation */}
        <div className="sipkr-dots" role="tablist" aria-label="Navigasi slide">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === current}
              aria-label={`Slide ${i + 1}`}
              className={`sipkr-dot ${i === current ? "sipkr-dot--active" : ""}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        {/* Hero content */}
        <div className="sipkr-hero-content">
          <span className="sipkr-hero-badge">Kabupaten Sumbawa Barat</span>
          <img
            src="/images/logobaru.svg"
            alt="Logo SIPKR"
            className="sipkr-logo"
          />
          <h1 className="sipkr-hero-title">
            SIDALIMAH<br />
            {/* <span className="sipkr-hero-accent">Pendataan Kualitas Rumah</span> */}
          </h1>
          <p className="sipkr-hero-sub">
          Sistem Informasi Digital Layanan dan Manajemen Perumahan
Platform Digital Terpadu untuk Pengelolaan Program Perumahan
Menguatkan Data, Mewujudkan Tata Kelola.
          </p>

          {/* Action buttons */}
        <nav className="sipkr-actions" aria-label="Navigasi utama">
  <Link to="/login" className="sipkr-btn sipkr-btn--primary">
    <Key className="sipkr-btn-icon" size={18} /> Login
  </Link>
  <a
    href="https://linktr.ee/eguidermhksb"
    className="sipkr-btn sipkr-btn--amber"
    target="_blank"
    rel="noopener noreferrer"
  >
    <HomeIcon className="sipkr-btn-icon" size={18} /> KSB Maju Perumahan
  </a>
  <Link to="/informasi-dasar-hukum" className="sipkr-btn sipkr-btn--ghost">
    <BookOpen className="sipkr-btn-icon" size={18} /> Dasar Hukum
  </Link>
  <a
    href="https://www.lapor.go.id/"
    className="sipkr-btn sipkr-btn--ghost"
    target="_blank"
    rel="noopener noreferrer"
  >
    <Mic className="sipkr-btn-icon" size={18} /> Lapor
  </a>
</nav>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      {/* <div className="sipkr-stats-bar" ref={statsRef}>
        {STATS.map((s) => (
          <StatCounter key={s.id} {...s} started={statsStarted} />
        ))}
      </div> */}

      {/* ── GRAFIK SECTION ── */}
      <main className="sipkr-main">
        <div className="sipkr-section-header">
          <span className="sipkr-eyebrow">Data Wilayah</span>
          <h2 className="sipkr-section-title">Grafik Seluruh Kecamatan</h2>
          <p className="sipkr-section-sub">
            Rekapitulasi kualitas rumah berdasarkan data terkini dari seluruh kecamatan di KSB.
          </p>
        </div>

        <GrafikSeluruhKecamatan />
      </main>

      <Footer />
    </>
  );
}